import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { formatNomenclatureWithSize } from '@equip/utils';

export interface BarcodeMapping {
  id: string;
  barcode_value: string;
  catalog_id: string;
  location_id: string;
  created_at: string;
  updated_at: string;
}

export interface BarcodeLabelRow {
  id: string;
  barcode_value: string;
  catalog_id: string;
  catalog_name: string;
  lin?: string;
  old_lin?: string;
  nsn?: string;
  color?: string;
  unit_of_issue?: string;
  part_number?: string;
  location_id: string;
  location_name: string;
  location_path: string;
  warehouse_location_name?: string;
  storefront_location_name?: string;
  created_at: string;
}

export class BarcodeService {
  /**
   * Get all barcode mappings with enriched catalog/location data for the barcode labels page.
   */
  async getAllMappings(): Promise<BarcodeLabelRow[]> {
    // Join barcode_mappings with catalog and location entities
    const mappings = await httpClient.get<
      Array<{
        id: string;
        barcode_value: string;
        catalog_id: string;
        location_id: string;
        created_at: string;
      }>
    >('/api/v1/barcode_mappings?order=created_at.desc');

    // Fetch catalog and location names in batched chunks to avoid
    // oversized response headers (Kong 502: "upstream sent too big header")
    const BATCH_SIZE = 50;
    const catalogIds = [...new Set(mappings.map((m) => m.catalog_id).filter(Boolean))];
    const locationIds = [...new Set(mappings.map((m) => m.location_id).filter(Boolean))];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const catalogChunks: Promise<Array<{ id: string; display_name: string; entity_data: any }>>[] =
      [];
    for (let i = 0; i < catalogIds.length; i += BATCH_SIZE) {
      const chunk = catalogIds.slice(i, i + BATCH_SIZE);
      catalogChunks.push(
        httpClient.get(
          `/api/v1/entities?id=in.(${chunk.join(',')})&select=id,display_name,entity_data`,
        ),
      );
    }

    const locationChunks: Promise<
      Array<{ id: string; display_name: string; breadcrumb_names: string[] }>
    >[] = [];
    for (let i = 0; i < locationIds.length; i += BATCH_SIZE) {
      const chunk = locationIds.slice(i, i + BATCH_SIZE);
      locationChunks.push(
        httpClient.get(
          `/api/v1/location_hierarchy?id=in.(${chunk.join(',')})&select=id,display_name,breadcrumb_names`,
        ),
      );
    }

    const [catalogResults, locationResults] = await Promise.all([
      Promise.all(catalogChunks),
      Promise.all(locationChunks),
    ]);
    const catalogs = catalogResults.flat();
    const locations = locationResults.flat();

    const catalogMap = new Map(catalogs.map((c) => [c.id, c]));
    const locationMap = new Map(locations.map((l) => [l.id, l]));

    // Collect warehouse/storefront location IDs from catalog entity_data to resolve names
    const extraLocationIds = new Set<string>();
    for (const catalog of catalogs) {
      const wh = catalog.entity_data?.secondary_location_id;
      const sf = catalog.entity_data?.primary_location_id;
      if (wh && !locationMap.has(wh)) extraLocationIds.add(wh);
      if (sf && !locationMap.has(sf)) extraLocationIds.add(sf);
    }

    if (extraLocationIds.size > 0) {
      const extraIds = [...extraLocationIds];
      const extraChunks: Promise<
        Array<{ id: string; display_name: string; breadcrumb_names: string[] }>
      >[] = [];
      for (let i = 0; i < extraIds.length; i += BATCH_SIZE) {
        const chunk = extraIds.slice(i, i + BATCH_SIZE);
        extraChunks.push(
          httpClient.get(
            `/api/v1/location_hierarchy?id=in.(${chunk.join(',')})&select=id,display_name,breadcrumb_names`,
          ),
        );
      }
      const extraResults = (await Promise.all(extraChunks)).flat();
      for (const loc of extraResults) {
        locationMap.set(loc.id, loc);
      }
    }

    return mappings.map((m) => {
      const catalog = catalogMap.get(m.catalog_id);
      const location = locationMap.get(m.location_id);

      const warehouseLocId = catalog?.entity_data?.secondary_location_id;
      const storefrontLocId = catalog?.entity_data?.primary_location_id;

      return {
        id: m.id,
        barcode_value: m.barcode_value,
        catalog_id: m.catalog_id,
        catalog_name: formatNomenclatureWithSize(
          catalog?.display_name || 'Unknown',
          (catalog?.entity_data as Record<string, unknown> | undefined)?.size as string | undefined,
        ),
        lin: catalog?.entity_data?.lin || catalog?.entity_data?.LIN,
        old_lin: catalog?.entity_data?.old_lin,
        nsn: catalog?.entity_data?.nsn || catalog?.entity_data?.NSN,
        color: catalog?.entity_data?.color,
        unit_of_issue: catalog?.entity_data?.unit_of_issue,
        part_number: catalog?.entity_data?.part_number,
        location_id: m.location_id,
        location_name: location?.display_name || 'Unknown',
        location_path: location?.breadcrumb_names?.join(' > ') || '',
        warehouse_location_name: warehouseLocId
          ? locationMap.get(warehouseLocId)?.display_name
          : undefined,
        storefront_location_name: storefrontLocId
          ? locationMap.get(storefrontLocId)?.display_name
          : undefined,
        created_at: m.created_at,
      };
    });
  }

  /**
   * Manually create a barcode mapping for a catalog item at a location.
   */
  async createMappingManual(catalogId: string, locationId: string): Promise<BarcodeMapping> {
    const endpoint = `${API_CONFIG.ENDPOINTS.RPC}/create_barcode_mapping_manual`;
    const payload = {
      p_catalog_id: catalogId,
      p_location_id: locationId,
    };
    const response = await httpClient.post<BarcodeMapping[]>(endpoint, payload);
    const mapping = response[0];
    if (!mapping) {
      throw new Error('Failed to create barcode mapping');
    }
    return mapping;
  }

  /**
   * Soft-delete a barcode mapping.
   */
  async deleteMapping(mappingId: string): Promise<void> {
    // Route deletes through update_entity_data so deleted_at updates are
    // validated and audited consistently with other entity soft-deletes.
    const endpoint = `${API_CONFIG.ENDPOINTS.RPC}/update_entity_data`;
    await httpClient.post(endpoint, {
      p_entity_id: mappingId,
      p_deleted_at: new Date().toISOString(),
    });
  }
}

export const barcodeService = new BarcodeService();
